import React, { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { signIn, useSession } from 'next-auth/react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useAppDispatch } from '@/core/store/store';
import { LoginStatesValues } from '@/core/constants/constants';

import { getUser } from '@/features/Auth/store';
import { AuthType } from '@/features/Auth/interfaces';
import { FormDataType, LoginForm } from '@/features/Auth/components/LoginForm';
import { AuthLayout } from '@/features/Auth/components/AuthLayout';

import { request } from '@/utils/request';
import { isG4S } from '@/utils/helpers';

const schema = yup.object().shape({
  email: yup.string().email().required('Email is a required field'),
  password: yup.string().required('Password is a required field'),
});

export const Login = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { data: sessionData, status } = useSession();

  const [formError, setFormError] = React.useState<string | null>(null);

  const formMethods = useForm<FormDataType>({
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: yupResolver(schema),
  });

  const pageTitle = useMemo(() => {
    if (isG4S) {
      return 'Welcome to GIS+';
    }

    return 'Welcome to Hozint';
  }, []);

  const { setError } = formMethods;

  const onSubmit = async (formData: FormDataType) => {
    setFormError(null);

    try {
      const res = await signIn<AuthType.CREDENTIALS>(AuthType.CREDENTIALS, {
        ...formData,
        redirect: false,
        callbackUrl: '/',
      });

      if (res?.error) {
        try {
          const errorJson = JSON.parse(res.error);

          let hasFieldError = false;

          const passwordError = errorJson.errors?.password ?? errorJson.password;

          if (passwordError) {
            const msg = Array.isArray(passwordError) ? passwordError.join(' ') : passwordError;

            setError('password', { message: msg });
            hasFieldError = true;
          }

          const emailError = errorJson.errors?.email ?? errorJson.email;

          if (emailError) {
            const msg = Array.isArray(emailError) ? emailError.join(' ') : emailError;

            setError('email', { message: msg });
            hasFieldError = true;
          }

          const serverMessage = errorJson.message ?? errorJson.detail;

          if (serverMessage) {
            setFormError(serverMessage);
          } else if (!hasFieldError) {
            setFormError('Something went wrong. Please try again.');
          }
        } catch {
          setFormError('Something went wrong. Please try again.');
        }

        return;
      }

      return res;
    } catch (error) {
      setFormError('Something went wrong. Please try again.');

      return null;
    }
  };

  useEffect(() => {
    const afterLogin = async () => {
      if (status === LoginStatesValues.AUTHENTICATED && sessionData?.accessToken) {
        request.setAuthorizationToken(sessionData?.accessToken);
        await dispatch(getUser());

        router.replace('/');
      }
    };

    afterLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <AuthLayout
      title={pageTitle}
      subtitle="Please login to proceed"
    >
      <FormProvider {...formMethods}>
        <LoginForm
          onSubmit={onSubmit}
          formError={formError}
        />
      </FormProvider>
    </AuthLayout>
  );
};
